import { Check, ChevronDown, Minus, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useId, useState } from 'react';
import { useTrackedElement } from './use-tracked-element';

export interface AdvisorCardDetailsProps {
  defaultExpanded: boolean;
  prosLabel: string;
  consLabel: string;
  prosBullets: string[];
  consBullets: string[];
}

export function AdvisorCardDetails({
  defaultExpanded,
  prosLabel,
  consLabel,
  prosBullets,
  consBullets,
}: AdvisorCardDetailsProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();
  const { ref, track } = useTrackedElement('advisor_product_card_details');

  if (!prosBullets.length && !consBullets.length) return null;

  function toggle() {
    track(expanded ? 'collapse' : 'expand');
    setExpanded(!expanded);
  }

  return (
    <div className="border-t border-border">
      <button
        aria-controls={panelId}
        aria-expanded={expanded}
        className="flex w-full items-center justify-center gap-1.5 bg-transparent py-2 text-sm font-semibold text-[var(--content-green)] transition-colors hover:bg-[var(--content-green-extra-light)] focus-visible:bg-[var(--content-green-extra-light)] focus-visible:focus-ring @xl:py-2.5"
        onClick={toggle}
        ref={ref}
        type="button"
      >
        {expanded ? 'Hide details' : 'View details'}
        <ChevronDown
          aria-hidden
          className={`size-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      <div hidden={!expanded} id={panelId}>
        <div className="grid gap-3 px-4 pt-1 pb-4 @xl:grid-cols-2 @xl:gap-5 @xl:px-5 @xl:pb-5 @3xl:px-6">
          <BulletList bullets={prosBullets} label={prosLabel} tone="pro" />
          <BulletList bullets={consBullets} label={consLabel} tone="con" />
        </div>
      </div>
    </div>
  );
}

function BulletList({
  bullets,
  label,
  tone,
}: {
  bullets: string[];
  label: string;
  tone: 'pro' | 'con';
}) {
  if (!bullets.length) return null;
  const isPro = tone === 'pro';
  const HeadingIcon = isPro ? ThumbsUp : ThumbsDown;
  const BulletIcon = isPro ? Check : Minus;

  return (
    <div
      className={`rounded-lg p-3 @xl:p-4 ${isPro ? 'bg-[var(--content-green-extra-light)]' : 'bg-[var(--content-neutral-lightest)]'}`}
    >
      <h4 className="m-0 flex items-center gap-1.5 font-sans text-xs font-bold tracking-wider uppercase">
        <HeadingIcon aria-hidden className="size-3.5" />
        {label}
      </h4>
      <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0 @xl:mt-3 @xl:gap-2.5">
        {bullets.map(bullet => (
          <li className="flex items-start gap-2 text-sm" key={bullet}>
            <span
              aria-hidden
              className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full text-white ${isPro ? 'bg-[var(--content-green)]' : 'bg-[var(--content-neutral-darker)]'}`}
            >
              <BulletIcon size={11} strokeWidth={3} />
            </span>
            {bullet}
          </li>
        ))}
      </ul>
    </div>
  );
}
